import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import AbsenceDialog from "../components/absences/AbsenceDialog";
import AbsencesPageHeader from "../components/absences/AbsencesPageHeader";
import AbsenceList from "../components/absences/AbsenceList";
import AbsencesMonthGrid from "../components/absences/AbsencesMonthGrid";
import ClosedPeriodBridgeDialog from "../components/absences/ClosedPeriodBridgeDialog";
import { selectAbsencesRange } from "../features/absences/absences-slice";
import { fetchAbsencesRange } from "../features/absences/slice-extensions/absences-slice-extension";
import { useAbsenceActions } from "../features/absences/use-absence-actions";
import { selectClosedDays } from "../features/closed-days/closed-days-slice";
import { fetchClosedDays } from "../features/closed-days/slice-extensions/closed-days-slice-extension";
import { useWeekStartPreference } from "../features/calendar/use-week-start-preference";
import { getMonthGridDays, toYmd } from "../features/calendar/utils/calendar-range";
import { selectMembers } from "../features/members/members-slice";
import { fetchMembers } from "../features/members/slice-extensions/members-slice-extension";

/** Availability overview: member absences and closed days on one month grid, with CRUD. */
const AbsencesPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const weekStart = useWeekStartPreference();
  const members = useAppSelector(selectMembers);
  const absences = useAppSelector(selectAbsencesRange);
  const closedDays = useAppSelector(selectClosedDays);
  const [anchor, setAnchor] = useState(() => new Date());

  const memberNameById = useMemo(
    () => new Map(members.map((member) => [member.id, `${member.firstName} ${member.lastName}`])),
    [members],
  );
  const actions = useAbsenceActions(memberNameById);

  const range = useMemo(() => {
    const days = getMonthGridDays(anchor, weekStart);
    return { from: toYmd(days[0]), to: toYmd(days[days.length - 1]) };
  }, [anchor, weekStart]);

  useEffect(() => {
    dispatch(fetchMembers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAbsencesRange(range));
    dispatch(fetchClosedDays(range));
  }, [dispatch, range]);

  const closedByDate = useMemo(
    () => new Map(closedDays.map((day) => [day.date, day])),
    [closedDays],
  );
  const monthLabel = new Intl.DateTimeFormat(i18n.language, {
    month: "long",
    year: "numeric",
  }).format(anchor);

  const shiftMonth = (offset: number) =>
    setAnchor((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full min-w-0 p-4 pb-10 lg:px-10">
          <AbsencesPageHeader
            monthLabel={monthLabel}
            onPreviousMonth={() => shiftMonth(-1)}
            onNextMonth={() => shiftMonth(1)}
            onNew={() => actions.setDialog({})}
          />

          <AbsencesMonthGrid
            anchor={anchor}
            absences={absences}
            memberNameById={memberNameById}
            closedByDate={closedByDate}
            weekStart={weekStart}
          />

          <h2 className="mb-3 mt-6 text-lg font-semibold text-grey-20 dark:text-white">
            {t("absences.listTitle", "Absences this month")}
          </h2>
          <AbsenceList
            absences={absences}
            memberNameById={memberNameById}
            onEdit={(absence) => actions.setDialog({ absence })}
            onDelete={actions.remove}
          />
        </div>
      </div>

      {actions.dialog && (
        <AbsenceDialog
          absence={actions.dialog.absence}
          members={members}
          busy={actions.busy}
          onClose={() => actions.setDialog(null)}
          onSubmit={actions.submit}
        />
      )}

      {actions.bridge.offer && (
        <ClosedPeriodBridgeDialog
          offer={actions.bridge.offer}
          busy={actions.bridge.busy}
          onConfirm={actions.bridge.confirm}
          onDismiss={actions.bridge.dismiss}
        />
      )}
    </section>
  );
};

export default AbsencesPage;
